import styled from 'styled-components';

/* ─────────────────────────────────────────
   HERO
   Gradient darkens toward the RIGHT (where
   the Arabic text overlay lives).
───────────────────────────────────────── */
export const HeroBanner = styled.section`
  position: relative;
  width: 100%;
  height: 455px;
  overflow: hidden;
  background: #1A1A1A;
  flex-shrink: 0;

  @media (max-width: 720px) { height: 320px; }
  @media (max-width: 480px) { height: 240px; }

  .hero-swiper,
  .hero-swiper .swiper-slide {
    width: 100%;
    height: 100%;
  }

  .hero-img {
    width: 100%;
    height: 100%;
    object-fit: stretch;
    object-position: center top;
    display: block;
  }

  /* Navigation arrows — match brand purple */
  .hero-swiper .swiper-button-next,
  .hero-swiper .swiper-button-prev {
    width: 38px;
    height: 38px;
    background: rgba(13, 13, 27, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 50%;
    color: #fff;
    transition: background 0.18s, border-color 0.18s;

    &::after {
      font-size: 15px;
      font-weight: 700;
    }

    &:hover {
      background: #9236E4;
      border-color: #9236E4;
    }

    @media (max-width: 480px) { display: none; }
  }

  /* Pagination dots — bottom center, above the text overlay */
  .hero-swiper .swiper-pagination {
    bottom: 14px;
  }

  .hero-swiper .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    background: rgba(255, 255, 255, 0.55);
    opacity: 1;
    transition: background 0.18s, width 0.18s;
  }

  .hero-swiper .swiper-pagination-bullet-active {
    background: #9236E4;
    width: 22px;
    border-radius: 4px;
  }

  /* Darkens the text side (inline-end) */
  .hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to inline-end,
      transparent 25%,
      rgba(13, 13, 27, 0.55) 55%,
      rgba(13, 13, 27, 0.88) 100%
    );
  }

  /* Text anchored to the inline-end corner */
  .hero-content {
    position: absolute;
    bottom: 36px;
    inset-inline-start: 28px;
    text-align: inherit;
    max-width: 400px;

    @media (max-width: 720px) {
      bottom: 20px;
      inset-inline-start: 16px;
      max-width: 280px;
    }
    @media (max-width: 480px) {
      bottom: 14px;
      inset-inline-start: 12px;
      max-width: 200px;
    }

    .status-badge {
      display: inline-block;
      background: #16a34a;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      padding: 3px 14px;
      border-radius: 4px;
      margin-bottom: 10px;
      font-family: inherit;
    }

    .hero-title {
      font-size: 26px;
      font-weight: 800;
      color: #fff;
      line-height: 1.25;
      margin-bottom: 10px;
      text-shadow: 0 2px 16px rgba(0, 0, 0, 0.7);
      @media (max-width: 720px) { font-size: 18px; }
      @media (max-width: 480px) { font-size: 14px; margin-bottom: 6px; }
    }

    .hero-desc {
      font-size: 12.5px;
      color: rgba(255, 255, 255, 0.72);
      line-height: 1.75;
      margin-bottom: 18px;
      @media (max-width: 720px) { display: none; }
    }

    .hero-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 22px;
      background: #9236E4;
      border: none;
      border-radius: 33px;
      color: #fff;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: background 0.18s;

      &:hover { background: #6d28d9; }
    }
  }
`;

/* ─────────────────────────────────────────
   SECTION HEADER ROW
───────────────────────────────────────── */
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 12px;

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
    color: #e2e8f0;

    svg {
      color: #9ca3af;
      font-size: 17px;
    }
  }

  .filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #2A2A2A;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    color: #9ca3af;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.18s, color 0.18s;

    &:hover {
      border-color: rgba(109, 40, 217, 0.5);
      color: #e2e8f0;
    }
  }
`;

/* ─────────────────────────────────────────
   SERVERS GRID  (4 columns)
───────────────────────────────────────── */
export const ServersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  padding: 0 20px 8px;

  @media (max-width: 1000px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 720px)  { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  @media (max-width: 480px)  { grid-template-columns: 1fr; padding: 0 12px 8px; }
`;

/* ─────────────────────────────────────────
   SERVER CARD
   Card body layout (direction: rtl, flex space-between):
     RIGHT col: server name, stats, level, متاح badge
     LEFT  col: heart+count, platform icon
   Bottom: full-width gold button
───────────────────────────────────────── */
export const ServerCard = styled.div`
  background: #1A1A1A;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  display: block;
  text-decoration: none;
  transition: border-color 0.18s, transform 0.15s;

  &:hover {
    border-color: rgba(109, 40, 217, 0.45);
    transform: translateY(-2px);
  }

  /* ── Card image ── */
  .card-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: #0d0d1b;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    /* Always physically top-left of image regardless of RTL */
    .rank-badge {
      position: absolute;
      top: 7px;
      left: 7px;
      background: #dc2626;
      color: #fff;
      font-size: 10px;
      font-weight: 800;
      padding: 2px 7px;
      border-radius: 4px;
      line-height: 1.4;
      letter-spacing: 0.02em;
    }

  }

  /* ── Card body ── */
  .card-body {
    padding: 9px 11px 11px;
    direction: rtl;
  }

  /* Top row: space-between in RTL puts right-col on RIGHT, left-col on LEFT */
  .card-top-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 3px;
  }

  .card-right-col {
    flex: 1;
    min-width: 0;
  }

  .card-left-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding-top: 2px;
  }

  /* Heart + count (keep LTR so heart left of number) */
  .card-likes {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #9ca3af;
    font-size: 11.5px;
    direction: ltr;

    svg { color: #e2e8f0; font-size: 13px; }
  }

  .platform-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: #5865f2;
    border-radius: 8px;
    color: #fff;
    font-size: 16px;
    box-shadow: 0 2px 8px rgba(88, 101, 242, 0.45);
  }

  .card-name {
    font-size: 12.5px;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-stats {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 2px;
  }

  .card-level {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 6px;
  }

  .card-owner {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }

  .owner-label {
    font-size: 11px;
    color: #9ca3af;
  }

  .fame-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 4px;
    line-height: 1.4;
    letter-spacing: 0.02em;

    svg { font-size: 10px; }

    &.known {
      background: rgba(59, 130, 246, 0.9);
      color: #fff;
    }
    &.famous {
      background: linear-gradient(135deg, #e8c05a 0%, #c09136 50%, #8a6520 100%);
      color: #1a1305;
    }
    &.unknown {
      background: rgba(156, 163, 175, 0.9);
      color: #fff;
    }
  }

  /* متاح badge (right-aligned in RTL) */
  .status-badge {
    display: inline-flex;
    margin-inline: 10px;
    align-items: center;
    background: rgba(22, 163, 74, 0.18);
    color: #4ade80;
    border: 1px solid rgba(74, 222, 128, 0.28);
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 4px;
    line-height: 1.5;
    font-family: inherit;
  }

  /* Full-width gold button at the bottom */
  .website-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: fit-content;
    margin: 10px auto 0;
    padding: 8px 10px;
    background: linear-gradient(135deg, #e8c05a 0%, #c09136 50%, #8a6520 100%);
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: filter 0.18s;

    svg { font-size: 12px; }

    &:hover { filter: brightness(0.88); }
  }
`;

/* ─────────────────────────────────────────
   CREATORS SECTION
───────────────────────────────────────── */
export const CreatorsSection = styled.section`
  background: #1F1E22;
  margin-top: 8px;
  padding-bottom: 28px;
`;

export const CreatorsRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 4px 20px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

export const CreatorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  /* flex-shrink: 0; */
  cursor: pointer;
  padding: 14px 12px 12px;
  /* border-radius: 14px; */
  /* background: #28272C; */
  /* border: 1px solid rgba(255, 255, 255, 0.06); */
  min-width: 90px;
  transition: background 0.18s, transform 0.18s, border-color 0.18s;

  &:hover {
    background: #2f2d35;
    border-color: rgba(255, 0, 0, 0.25);
    transform: translateY(-3px);
  }

  .avatar-container {
    position: relative;
  }

  .avatar-wrap {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(135deg, #ff4444, #cc0000);

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
      display: block;
      border: 2px solid #28272C;
    }
  }

  .creator-platform {
    height: 20px;
    width: 20px;
    background: #ff0000;
    border-radius: 50%;
    border: 2px solid #28272C;
    display: flex;
    align-items: center;
    justify-content: center;

    svg { color: #fff; font-size: 9px; }
  }

  .creator-name {
    font-size: 11.5px;
    font-weight: 600;
    color: #e2e8f0;
    text-align: center;
    max-width: 80px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .creator-sub {
    font-size: 10px;
    color: #6b7280;
    text-align: center;
  }
`;

/* ─────────────────────────────────────────
   LEGACY STUBS — used by serverDetails page
───────────────────────────────────────── */
export const HomeWrapper = styled.div``;
export const MainContent = styled.main``;
export const ServerSection = styled.section``;
export const Pagination = styled.div``;
export const StatsSection = styled.section``;
export const WhySection = styled.section``;
export const ContentSection = styled.section``;
export const SidebarCol = styled.aside``;
export const HomeNav = styled.nav``;
export const HomeFooter = styled.footer``;
export const LineSeparator = styled.div``;
